const USER_PREFERENCES_STORAGE_KEY = 'jtl-dashboard-user-preferences-v1';
const DASHBOARD_UI_STATE_KEY = 'dashboard_ui_state_v1';
const TENANT_SELECTION_STORAGE_PREFIX = 'dashboard_active_tenant:';

export const NOTIFICATION_CATEGORY_DEFAULTS = {
    stock: true,
    products: true,
    returns: true,
    profit: true,
    channels: true
};

const NOTIFICATION_DELIVERY_MODES = new Set(['instant', 'window', 'muted']);

const DEFAULT_USER_PREFERENCES = {
    theme: 'dark',
    browserNotificationsEnabled: false,
    notificationDeliveryMode: 'instant',
    notificationWindowStart: '08:00',
    notificationWindowEnd: '18:00',
    notificationCategories: NOTIFICATION_CATEGORY_DEFAULTS
};

const getUserPreferencesStorageKey = (userId = '') => (
    userId ? `${USER_PREFERENCES_STORAGE_KEY}:${userId}` : USER_PREFERENCES_STORAGE_KEY
);

const isValidTimeValue = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ''));

export const normalizeUserPreferences = (preferences = {}) => {
    const requestedMode = String(preferences.notificationDeliveryMode || DEFAULT_USER_PREFERENCES.notificationDeliveryMode);
    const categories = preferences.notificationCategories && typeof preferences.notificationCategories === 'object'
        ? preferences.notificationCategories
        : {};

    return {
        theme: preferences.theme === 'light' ? 'light' : 'dark',
        browserNotificationsEnabled: Boolean(preferences.browserNotificationsEnabled),
        notificationDeliveryMode: NOTIFICATION_DELIVERY_MODES.has(requestedMode) ? requestedMode : DEFAULT_USER_PREFERENCES.notificationDeliveryMode,
        notificationWindowStart: isValidTimeValue(preferences.notificationWindowStart) ? preferences.notificationWindowStart : DEFAULT_USER_PREFERENCES.notificationWindowStart,
        notificationWindowEnd: isValidTimeValue(preferences.notificationWindowEnd) ? preferences.notificationWindowEnd : DEFAULT_USER_PREFERENCES.notificationWindowEnd,
        notificationCategories: Object.fromEntries(
            Object.entries(NOTIFICATION_CATEGORY_DEFAULTS).map(([key, fallback]) => [key, categories[key] === undefined ? fallback : Boolean(categories[key])])
        )
    };
};

const timeToMinutes = (value) => {
    const [hours, minutes] = String(value || '00:00').split(':').map(part => Number.parseInt(part, 10));
    return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
};

export const isInsideNotificationWindow = (preferences, date = new Date()) => {
    const start = timeToMinutes(preferences.notificationWindowStart);
    const end = timeToMinutes(preferences.notificationWindowEnd);
    const now = date.getHours() * 60 + date.getMinutes();

    if (start === end) return true;
    if (start < end) return now >= start && now <= end;
    return now >= start || now <= end;
};

export const getNotificationPreferenceCategory = (alert = {}) => {
    const id = String(alert.id || '').toLowerCase();
    const area = String(alert.area || '').toLowerCase();

    if (id.includes('return') || area.includes('retour')) return 'returns';
    if (id.includes('top-products') || area.includes('produkt')) return 'products';
    if (id.includes('stock') || area.includes('lager')) return 'stock';
    if (id.includes('channel') || area.includes('kan')) return 'channels';
    if (id.includes('margin') || area.includes('profit')) return 'profit';
    return 'profit';
};

export const readUserPreferences = (userId = '') => {
    if (typeof window === 'undefined') return DEFAULT_USER_PREFERENCES;
    try {
        const raw = window.localStorage.getItem(getUserPreferencesStorageKey(userId))
            || window.localStorage.getItem(USER_PREFERENCES_STORAGE_KEY);
        return normalizeUserPreferences(raw ? JSON.parse(raw) : DEFAULT_USER_PREFERENCES);
    } catch {
        return DEFAULT_USER_PREFERENCES;
    }
};

export const writeUserPreferences = (preferences, userId = '') => {
    const next = normalizeUserPreferences(preferences);
    if (typeof window !== 'undefined') {
        try {
            window.localStorage.setItem(getUserPreferencesStorageKey(userId), JSON.stringify(next));
        } catch {
            // Ignore localStorage write failures.
        }
    }
    return next;
};

export const readDashboardUiState = () => {
    if (typeof window === 'undefined') return null;

    try {
        const raw = window.localStorage.getItem(DASHBOARD_UI_STATE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const writeDashboardUiState = (state) => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(DASHBOARD_UI_STATE_KEY, JSON.stringify(state));
    } catch {
        // Ignore localStorage issues.
    }
};

export const readStoredTenantId = (userId) => {
    if (typeof window === 'undefined' || !userId) return '';

    try {
        return window.localStorage.getItem(`${TENANT_SELECTION_STORAGE_PREFIX}${userId}`) || '';
    } catch {
        return '';
    }
};

export const writeStoredTenantId = (userId, tenantId) => {
    if (typeof window === 'undefined' || !userId || !tenantId) return;

    try {
        window.localStorage.setItem(`${TENANT_SELECTION_STORAGE_PREFIX}${userId}`, tenantId);
    } catch {
        // Ignore localStorage issues.
    }
};
